export interface SharedStructure {
  buffer: ArrayBuffer;
  name: string;
}

export interface ShareLink {
  id: string;
  url: string;
}

/** 共有IDから、いま開いているオリジンでのURLを組み立てる。 */
export function shareUrlFor(id: string): string {
  return new URL(`/s/${id}`, window.location.origin).toString();
}

/** アドレスが`/s/<id>`なら、その共有IDを返す。 */
export function sharedIdFromLocation(pathname: string): string | null {
  const match = /^\/s\/([2-9a-z]{6,32})\/?$/.exec(pathname);
  return match ? match[1] : null;
}

/**
 * ファイル名は`x-structure-name`ヘッダで運ぶ。ヘッダにはISO-8859-1の文字しか載せられないので、
 * 「ぼくのいえ.mcstructure」のような日本語名でも送れるよう%エンコードした形でやり取りする。
 */
function decodeStructureName(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch {
    // 壊れた%列で読み込みを止めたくないので、そのときはヘッダの値をそのまま名前にする。
    return raw;
  }
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json() as { error?: string };
    return body.error ?? fallback;
  } catch {
    return fallback;
  }
}

/** 建築データを保存し、共有用のURLを受け取る。 */
export async function createShareLink(buffer: ArrayBuffer, name: string): Promise<ShareLink> {
  const response = await fetch('/api/share', {
    method: 'POST',
    headers: { 'content-type': 'application/octet-stream', 'x-structure-name': encodeURIComponent(name) },
    body: buffer,
  });
  if (!response.ok) throw new Error(await errorMessage(response, '共有リンクを作れませんでした。'));

  const body = await response.json() as { id?: string };
  if (!body.id) throw new Error('共有リンクを作れませんでした。');
  return { id: body.id, url: shareUrlFor(body.id) };
}

export async function fetchSharedStructure(id: string): Promise<SharedStructure> {
  const response = await fetch(`/api/share/${id}`);
  if (!response.ok) throw new Error(await errorMessage(response, '共有された建築を読み込めませんでした。'));

  return {
    buffer: await response.arrayBuffer(),
    name: decodeStructureName(response.headers.get('x-structure-name'), 'shared.nbt'),
  };
}
